import React, { useContext, useEffect } from 'react';
import moment from 'moment';
import { CustomerCreatUpdate, resetCustomerSetFormValue, setCustomerItem, setCustomerItems, setCustomerSetFormValue } from '../state/customer.slice';
import { CustomerFormData } from '../beans/customer.form.data';
import { IntlContext } from 'react-intl';
import { ContextEnum } from 'src/_shared/enums/context.enum';
import { FormManager } from 'src/_shared/managers/form.manager';
import { NavigationManager } from 'src/_shared/managers/navigation.manager';
import { PopupManager } from 'src/_shared/managers/popup.manager';
import { QueueManager, QueueKeyType } from 'src/_shared/managers/queue.manager';
import { UserManager } from 'src/_shared/managers/user.manager';
import { RouteEnum } from 'src/_shared/routes';
import { store } from 'src/_shared/store/configure';
import { useStoreSelector } from 'src/_shared/store/hook';
import { FormHelperUtil } from 'src/_shared/utils/form.helper.util';
import { PopupType } from 'src/features/_shared/components/Popup/popup.item';
import { TextField } from 'src/features/_shared/components/TextField';
import { User } from 'src/api/entities';
import { Button } from 'src/features/_shared/components/Button/button.component';
import { PageTransition } from 'src/features/_shared/components/PageTransition/page.transition.component';
import { EmptyState } from 'src/features/_shared/components/EmptyState/empty.state.component';

export const CustomersView: React.FC = () => {
    const customerManager = UserManager.getInstance();
    const queueManager = QueueManager.getInstance();
    const notificationManager = PopupManager.getInstance();
    const navigationManager = NavigationManager.getInstance();
    const customers: User[] = useStoreSelector(state => state.reducer.customerSlice.customers);
    const intl = useContext(IntlContext);

    const userManager = UserManager.getInstance();
    const customerCreateUpdate: CustomerCreatUpdate = useStoreSelector(state => state.reducer.customerSlice.customerCreateUpdate);
    const contextState: ContextEnum = useStoreSelector(state => state.reducer.customerSlice.context);
    const getMessageByFieldAndError = FormHelperUtil.getFormFieldErrorTranslator('customer.create.form', intl as any);

    const searchUsers = async () => {
        const getItems = async () => {
            const value = (await customerManager.search()).user!;
            store.dispatch(setCustomerItems({ value }));
        };
        getItems();
    };

    useEffect(() => {
        searchUsers();
    }, []);

    const handleSubmit = async () => {
        console.log(customerCreateUpdate);
        const isFormValid = await trigger();
        if (isFormValid) {
            await userManager.create({
                email: getValues('email'),
                firstName: getValues('firstName'),
                lastName: getValues('lastName'),
                password: getValues('password'),
                role: 'ROLE_PERSONAL',
            });
            await searchUsers();

            // notificationManager.show({
            //     title: 'Cliente creato con successo',
            //     type: PopupType.Info,
            // });
            // resetCustomerCreateUpdateForm();
            notificationManager.hide();
            resetCustomerCreateUpdateForm();
        }
    };

    const { errors, register, trigger, getValues, control, setValue } = FormManager.getInstance().buildFormProxy<CustomerFormData>(
        CustomerFormData,
        handleSubmit,
        {
            email: customerCreateUpdate.email,
            password: customerCreateUpdate.password,
            firstName: customerCreateUpdate.firstName,
            lastName: customerCreateUpdate.lastName,
        },
        'onChange',
    );

    // called when an input loses focus; also kick off validation for the
    // single field so errors become visible without waiting for submit
    const handleOnBlur = async (key: any, value: string) => {
        if (contextState === ContextEnum.CREATE) {
            store.dispatch(setCustomerSetFormValue({ key, value }));
        }
        await trigger(key as any);
    };

    const resetCustomerCreateUpdateForm = () => {
        setValue('email', '');
        setValue('password', '');
        setValue('firstName', '');
        setValue('lastName', '');
        store.dispatch(resetCustomerSetFormValue());
        errors.email = undefined;
        errors.password = undefined;
        errors.firstName = undefined;
        errors.lastName = undefined;
    };

    const handleDelete = async (email: string) => {
        await userManager.delete(email), await searchUsers();
    };

    const handleStartCreate = () => {
        notificationManager.show({
            title: 'Nuovo cliente',
            type: PopupType.Info,
            icon: 'bi-people-fill',
            children: (
                <div className='App mt-2'>
                    <div className='col'>
                        <div className='d-flex flex-column gap-3 mb-3'>
                            <TextField
                                type='text'
                                placeholder='Email'
                                id='email'
                                {...register('email', {
                                    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                                        handleOnBlur(e.target.name, e.target.value);
                                    },
                                })}
                                fullWidth
                                error={errors.email}
                                errorMessage={errors.email && "Campo email non valido"}
                            />
                            <TextField
                                type='text'
                                placeholder='Password'
                                id='password'
                                {...register('password', {
                                    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                                        handleOnBlur(e.target.name, e.target.value);
                                    },
                                })}
                                fullWidth
                                error={errors.password}
                                errorMessage={errors.password && "Campo password non valido"}
                            />
                        </div>
                        <div className='d-flex flex-column gap-3 mb-2'>
                            <TextField
                                type='text'
                                placeholder='Nome'
                                id='firstName'
                                {...register('firstName', {
                                    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                                        handleOnBlur(e.target.name, e.target.value);
                                    },
                                })}
                                fullWidth
                                error={errors.firstName}
                                errorMessage={errors.firstName && "Campo nome non valido"}
                            />
                            <TextField
                                type='text'
                                placeholder='Cognome'
                                id='lastName'
                                {...register('lastName', {
                                    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                                        handleOnBlur(e.target.name, e.target.value);
                                    },
                                })}
                                fullWidth
                                error={errors.lastName}
                                errorMessage={errors.lastName && "Campo cognome non valido"}
                            />
                        </div>
                    </div>
                </div>
            ),
            ctas: {
                primary: {
                    cta: async () => {
                        await handleSubmit();
                    },
                },
                secondary: {
                    cta: () => {
                        resetCustomerCreateUpdateForm();
                        notificationManager.hide();
                    },
                },
                close: {
                    cta: () => {
                        resetCustomerCreateUpdateForm();
                        notificationManager.hide();
                    },
                },
            },
        });
    };

    const handleInspecSessions = async (customerID: number) => {
        const value: User = await queueManager.queue(QueueKeyType.DEFAULT, customerManager.getUserById(customerID));
        store.dispatch(setCustomerItem({ value }));
        navigationManager.navigateTo(RouteEnum.customersSessions);
    };

    const handleInspectAnalysis = async (customerID: number) => {
        const value: User = await queueManager.queue(QueueKeyType.DEFAULT, customerManager.getUserById(customerID));
        store.dispatch(setCustomerItem({ value }));
        navigationManager.navigateTo(RouteEnum.customersAnalysis);
    };

    const handleInspectWorkouts = (value: User) => {
        // queueManager.queue(QueueKeyType.DEFAULT, workoutManager.search(value.id!))
        store.dispatch(setCustomerItem({ value }));
        navigationManager.navigateTo(RouteEnum.customersWorkouts);
    };

    const getInitials = (customer: User) => {
        const first = customer.data?.details?.firstName?.[0] ?? '';
        const last = customer.data?.details?.lastName?.[0] ?? '';
        return (first + last).toUpperCase() || '?';
    };

    const avatarColors = ['#ff4949', '#D93955', '#e06040', '#c74a72', '#b05ca6', '#7b68c9', '#4a8fd9'];

    const getAvatarColor = (customer: User) => {
        const id = customer.id ?? 0;
        return avatarColors[id % avatarColors.length];
    };

    return (
        <PageTransition className='mt-4 px-3 px-md-4'>
            {/* ── Page Header ──────────────────────────────────────────── */}
            <div
                className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 p-4 anim-slide-up'
                style={{
                    background: 'linear-gradient(135deg, #fff 0%, #fafafa 100%)',
                    borderRadius: 12,
                    border: '1px solid rgba(0,0,0,0.06)',
                }}
            >
                <div>
                    <div className='d-flex align-items-center gap-2 mb-1'>
                        <i className='bi bi-people-fill' style={{ fontSize: '1.4rem', color: '#ff4949' }}></i>
                        <h4 className='mb-0 fw-bold' style={{ color: '#333' }}>Clienti</h4>
                    </div>
                    <p className='mb-0 mt-1' style={{ color: '#666', fontSize: '0.9rem' }}>
                        {customers.length > 0 ? (
                            <>
                                <span className='badge rounded-pill me-2' style={{ backgroundColor: '#ff4949', color: '#fff', fontSize: '0.8rem' }}>
                                    {customers.length}
                                </span>
                                utenti registrati
                            </>
                        ) : (
                            'Nessun cliente registrato'
                        )}
                    </p>
                </div>
                <div className='mt-3 mt-md-0'>
                    <Button
                        identifier='Nuovo cliente'
                        icon={<i className='bi bi-plus-lg' style={{ fontSize: '1rem', color: 'white' }}></i>}
                        onClick={handleStartCreate}
                        variant='primary'
                    />
                </div>
            </div>

            {/* ── Customer List ─────────────────────────────────────────── */}
            {customers.length > 0 ? (
                <div className='d-flex flex-column gap-3 mb-4'>
                    {customers.map((customer: User, index: number) => (
                        <div
                            className='app-card anim-stagger'
                            key={customer.id || index}
                            style={{
                                '--anim-index': index,
                                borderLeft: '3px solid #ff4949',
                                padding: 0,
                                overflow: 'hidden',
                            } as React.CSSProperties}
                        >
                            <div className='d-flex flex-column flex-lg-row align-items-lg-center justify-content-between p-3'>
                                {/* ── Left: Avatar + Info ─── */}
                                <div className='d-flex align-items-center gap-3 flex-grow-1'>
                                    {/* Avatar */}
                                    <div
                                        style={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: '50%',
                                            backgroundColor: getAvatarColor(customer),
                                            color: '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 700,
                                            fontSize: '0.95rem',
                                            flexShrink: 0,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        {getInitials(customer)}
                                    </div>

                                    {/* Name + meta */}
                                    <div className='d-flex flex-column' style={{ minWidth: 0 }}>
                                        <div className='d-flex align-items-center gap-2 flex-wrap'>
                                            <h6 className='mb-0 fw-semibold' style={{ color: '#222', fontSize: '1rem' }}>
                                                {customer.data?.details?.firstName} {customer.data?.details?.lastName}
                                            </h6>
                                            {customer.active !== undefined && (
                                                <span
                                                    className='badge rounded-pill'
                                                    style={{
                                                        backgroundColor: customer.active ? '#e6f9ee' : '#fef2f2',
                                                        color: customer.active ? '#1a7f42' : '#b91c1c',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {customer.active ? 'Attivo' : 'Inattivo'}
                                                </span>
                                            )}
                                        </div>
                                        {customer.email && (
                                            <span style={{ color: '#888', fontSize: '0.82rem', marginTop: 2 }}>
                                                <i className='bi bi-envelope me-1' style={{ fontSize: '0.75rem' }}></i>
                                                {customer.email}
                                            </span>
                                        )}
                                        <div className='d-flex align-items-center gap-3 mt-1 flex-wrap'>
                                            {customer.createdAt && (
                                                <span style={{ color: '#aaa', fontSize: '0.78rem' }}>
                                                    <i className='bi bi-calendar3 me-1' style={{ fontSize: '0.7rem' }}></i>
                                                    Registrato il {moment(customer.createdAt).format('D MMM YYYY')}
                                                </span>
                                            )}
                                            {customer.data?.details?.initials && (
                                                <span
                                                    className='badge rounded-pill'
                                                    style={{ backgroundColor: '#f2f2f2', color: '#666', fontSize: '0.72rem', fontWeight: 500 }}
                                                >
                                                    {customer.data.details.initials}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* ── Right: Actions ─── */}
                                <div className='d-flex align-items-center gap-2 mt-3 mt-lg-0 ms-lg-3 flex-shrink-0 flex-wrap'>
                                    <Button
                                        icon={<i className='bi bi-bar-chart-line-fill' style={{ fontSize: '1rem', color: 'white' }}></i>}
                                        identifier='Analisi'
                                        variant='primary'
                                        onClick={() => handleInspectAnalysis(customer.id!)}
                                    />
                                    <Button
                                        icon={<i className='bi bi-calendar-check-fill' style={{ fontSize: '1rem', color: 'white' }}></i>}
                                        identifier='Sedute'
                                        variant='primary'
                                        onClick={() => handleInspecSessions(customer.id!)}
                                    />
                                    <Button
                                        icon={<i className='bi bi-clipboard2-pulse-fill' style={{ fontSize: '1rem', color: 'white' }}></i>}
                                        identifier='Protocolli'
                                        variant='primary'
                                        onClick={() => handleInspectWorkouts(customer)}
                                    />
                                    <Button
                                        icon={<i className='bi bi-trash3-fill' style={{ fontSize: '1rem' }}></i>}
                                        variant='secondary'
                                        onClick={() => handleDelete(customer.email!)}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <EmptyState
                    icon='bi bi-people'
                    title='Nessun cliente'
                    description='Non ci sono ancora clienti registrati. Crea il primo cliente per iniziare.'
                    action={<Button identifier='Nuovo cliente' onClick={handleStartCreate} variant='primary' />}
                />
            )}
        </PageTransition>
    );
};
